/**
 * SessionsList Sub-component for ContextLinkingButton
 * Displays recent sessions in a dropdown menu for linking to Evidence queries.
 * MobileSessionsList variant uses button elements for bottom sheet compatibility.
 */

import { type LinkedSessionContext } from '@/atoms/chatAtoms';
import { Button } from '@heidi/design-system/button';
import { DropdownMenuItem } from '@heidi/design-system/dropdown-menu';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { Loader } from '@heidi/snowflake-ui/loader';
import { useQueryAdminSessions } from '@/hooks/query/useQuerySessionsV2';
import { cn } from '@/lib/cn';
import { Check, FileText, UserRound } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { contextLinkingSessionsListMessages } from './ContextLinkingSessionsList.messages';
import { formatSessionDate } from './formatContextLinkingSessionDate';

export { formatSessionDate } from './formatContextLinkingSessionDate';

interface SessionsListProps {
  linkedSession: LinkedSessionContext | null;
  onSelect: (session: LinkedSessionContext) => void;
  onClear: () => void;
  /** When true, disables selection and clear (context is locked after chat starts) */
  disabled?: boolean;
}

/** Shared session item data for render props */
interface SessionItemData {
  sessionId: string;
  sessionName: string;
  isLinked: boolean;
  patientName?: string;
  createdAt?: string;
  onItemClick: (() => void) | undefined;
}

/** Hook that encapsulates shared session list data fetching logic */
function useSessionsListData(
  linkedSession: LinkedSessionContext | null,
  onSelect: (session: LinkedSessionContext) => void,
  onClear: () => void,
  disabled: boolean
) {
  const intl = useIntl();
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useQueryAdminSessions();

  const sessions = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.sessions);
  }, [data?.pages]);

  const sessionItems: SessionItemData[] = useMemo(() => {
    return sessions.map((session) => {
      const isLinked = linkedSession?.id === session.session_id;
      const sessionName =
        session.session_name ||
        intl.formatMessage(contextLinkingSessionsListMessages.untitledSession);

      return {
        sessionId: session.session_id,
        sessionName,
        isLinked,
        patientName: session.patient?.name ?? undefined,
        createdAt: session.created_time_local ?? undefined,
        onItemClick: disabled
          ? undefined
          : isLinked
            ? onClear
            : () =>
                onSelect({
                  id: session.session_id,
                  name: sessionName,
                  patientName: session.patient?.name ?? undefined,
                  createdAt: session.created_time_local ?? undefined,
                }),
      };
    });
  }, [sessions, linkedSession?.id, disabled, onClear, onSelect, intl]);

  return {
    sessionItems,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  };
}

/** Shared empty state component */
const SessionsEmptyState = ({ className }: { className?: string }) => (
  <div className={cn('px-3 text-center', className)}>
    <TypographyP2 className="text-text-secondary">
      <FormattedMessage {...contextLinkingSessionsListMessages.noSessionsFound} />
    </TypographyP2>
  </div>
);

/** Shared loading state component */
const SessionsLoadingState = ({
  loaderSize,
  className,
}: {
  loaderSize: number;
  className?: string;
}) => (
  <div className={cn('flex items-center justify-center', className)}>
    <Loader size={loaderSize} />
  </div>
);

/** Props for session item content - shared between desktop and mobile */
interface SessionItemContentProps {
  sessionName: string;
  isLinked: boolean;
  patientName?: string;
  createdAt?: string;
  iconSize: number;
  checkSize: number;
  userIconSize: number;
  detailsPaddingStart: string;
}

/** Shared session item content */
const SessionItemContent = ({
  sessionName,
  isLinked,
  patientName,
  createdAt,
  iconSize,
  checkSize,
  userIconSize,
  detailsPaddingStart,
}: SessionItemContentProps) => {
  const intl = useIntl();

  return (
    <>
      <div className="flex w-full items-center gap-2">
        <FileText size={iconSize} className="shrink-0 text-text-secondary" />
        <TypographyP2 className="flex-1 truncate">{sessionName}</TypographyP2>
        {isLinked && <Check size={checkSize} className="shrink-0 text-text-primary" />}
      </div>
      <div className={cn('flex w-full items-center gap-3', detailsPaddingStart)}>
        {patientName && (
          <TypographyCaption className="flex items-center gap-1 text-text-tertiary">
            <UserRound size={userIconSize} />
            {patientName}
          </TypographyCaption>
        )}
        {createdAt && (
          <TypographyCaption className="text-text-tertiary">
            {formatSessionDate(createdAt, intl)}
          </TypographyCaption>
        )}
      </div>
    </>
  );
};

/** Shared load more button content */
const LoadMoreContent = ({
  isFetchingNextPage,
  loaderSize,
}: {
  isFetchingNextPage: boolean;
  loaderSize: number;
}): ReactNode => {
  const intl = useIntl();

  return isFetchingNextPage ? (
    <Loader size={loaderSize} />
  ) : (
    intl.formatMessage(contextLinkingSessionsListMessages.loadMore)
  );
};

export const SessionsList = ({
  linkedSession,
  onSelect,
  onClear,
  disabled = false,
}: SessionsListProps) => {
  const { sessionItems, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useSessionsListData(linkedSession, onSelect, onClear, disabled);

  if (isLoading) {
    return <SessionsLoadingState loaderSize={20} className="py-6" />;
  }

  if (sessionItems.length === 0) {
    return <SessionsEmptyState className="py-6" />;
  }

  return (
    <div>
      {sessionItems.map((item) => (
        <DropdownMenuItem
          key={item.sessionId}
          onClick={item.onItemClick}
          disabled={disabled}
          className={cn(
            'flex flex-col items-start gap-0.5 py-2',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        >
          <SessionItemContent
            sessionName={item.sessionName}
            isLinked={item.isLinked}
            patientName={item.patientName}
            createdAt={item.createdAt}
            iconSize={14}
            checkSize={14}
            userIconSize={10}
            detailsPaddingStart="ps-5"
          />
        </DropdownMenuItem>
      ))}

      {hasNextPage && (
        <DropdownMenuItem
          onClick={() => void fetchNextPage()}
          onSelect={(e) => e.preventDefault()}
          disabled={isFetchingNextPage}
          className="justify-center text-text-secondary"
        >
          <LoadMoreContent isFetchingNextPage={isFetchingNextPage} loaderSize={14} />
        </DropdownMenuItem>
      )}
    </div>
  );
};

/**
 * Mobile-optimized sessions list for bottom sheet
 * Uses button elements instead of DropdownMenuItem for drawer compatibility
 */
export const MobileSessionsList = ({
  linkedSession,
  onSelect,
  onClear,
  disabled = false,
}: SessionsListProps) => {
  const { sessionItems, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useSessionsListData(linkedSession, onSelect, onClear, disabled);

  if (isLoading) {
    return <SessionsLoadingState loaderSize={24} className="py-8" />;
  }

  if (sessionItems.length === 0) {
    return <SessionsEmptyState className="py-8" />;
  }

  return (
    <div className="flex flex-col">
      {sessionItems.map((item) => (
        <button
          key={item.sessionId}
          type="button"
          onClick={item.onItemClick}
          disabled={disabled}
          className={cn(
            'flex w-full flex-col items-start gap-1 rounded-lg p-3 text-start',
            'hover:bg-surface-hover active:bg-surface-pressed',
            disabled && 'cursor-not-allowed opacity-50'
          )}
        >
          <SessionItemContent
            sessionName={item.sessionName}
            isLinked={item.isLinked}
            patientName={item.patientName}
            createdAt={item.createdAt}
            iconSize={16}
            checkSize={16}
            userIconSize={10}
            detailsPaddingStart="ps-6"
          />
        </button>
      ))}

      {hasNextPage && (
        <Button size="lg"
          type="button"
          variant="ghost"
          onClick={() => void fetchNextPage()}
          disabled={isFetchingNextPage}
          className="mx-3 my-2 justify-center text-text-secondary"
        >
          <LoadMoreContent isFetchingNextPage={isFetchingNextPage} loaderSize={16} />
        </Button>
      )}
    </div>
  );
};
